// ABOUTME: Registry of pre-built dashboard "elements" — existing warehouse widgets (KPI cards, charts, tables)
// ABOUTME: embeddable in a BI dashboard cell. Sources map to one RTK hook each (fetches dedupe); elements
// ABOUTME: are either `source`-fed (card gets data as a prop) or `selfFetching` (owns its own query).

// Sourced KPI cards (fed a data prop by the renderer):
import {
    OrdersTodayToProcessCard,
    OrdersFutureToProcessCard,
    OrdersActionRequiredCard,
    OrdersB2BCard,
} from "../../../../environments/warehouse/outbound/orders/components/OrdersTopRowCards";
import { InboundsTotals } from "../../../../environments/warehouse/inbound/inbounds/components/InboundsCards";
import {
    ServiceTicketDeltaCard,
    ServiceUnassignedCard,
    ServiceNeedsReviewCard,
    ServiceLengthOfStayCard,
    ServiceResponsibilityCard,
} from "../../../../environments/warehouse/relation/service/components/ServiceCards";
import {
    LocationsPickInUseCard,
    LocationsPickGradeCard,
    LocationsBulkInUseCard,
    LocationsBulkGradeCard,
} from "../../../../environments/warehouse/stock/locations/components/LocationsCards";

// Self-fetching elements (own their query):
import { DashboardDeltaPanel } from "../../../../environments/warehouse/dashboard/components/DashboardDeltaPanel";
import { OrdersChart } from "../../../charts/dashboard/fulfillment/OrdersChart";
import { OrderlinesChart } from "../../../charts/dashboard/fulfillment/OrderlinesChart";
import { OrderHistoryChart } from "../../../charts/dashboard/fulfillment/OrderHistoryChart";
import { InboundChart } from "../../../charts/dashboard/fulfillment/InboundChart";
import { ReturnChart } from "../../../charts/dashboard/fulfillment/ReturnChart";
import { DaytotalsKpiStrip } from "../../../../environments/warehouse/reports/daytotals/components/DaytotalsKpiStrip";
import { DaytotalsProgressionChart } from "../../../../environments/warehouse/reports/daytotals/components/DaytotalsProgressionChart";
import { DaytotalsExpectedInbounds } from "../../../../environments/warehouse/reports/daytotals/components/DaytotalsExpectedInbounds";
import { DaytotalsEmployeeTable } from "../../../../environments/warehouse/reports/daytotals/Daytotals";

// Source hooks (one fetch each; RTK dedupes identical hook+args across cells):
import { useGetDashboardOrderTotalsQuery } from "../../../../newapi/order/ordersSlice";
import { useGetSupportTicketDashboardQuery } from "../../../../newapi/warehouse/supportTicketApiSlice";
import { useGetDashboardWarehouseLocationTotalsQuery } from "../../../../newapi/warehouse/warehouselocationSlice";
import { useGetDashboardInboundTotalsQuery } from "../../../../newapi/inbound/inboundsSlice";

// Current calendar month as yyyy-MM-dd strings — deterministic per day so RTK dedupes across cells.
const monthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return { startDate: fmt(start), endDate: fmt(end) };
};

// Subject groups — what the widget is ABOUT. Values match the report-type values where they overlap
// (orders / inbounds / returns / warehouselocations) so a single left-column chip lists both a subject's
// reports and its pre-built widgets; `service` and `daytotals` have no report type and stand alone.
export const dashboardElementGroups = {
    ORDERS: "orders",
    INBOUNDS: "inbounds",
    RETURNS: "returns",
    LOCATIONS: "warehouselocations",
    SERVICE: "service",
    DAYTOTALS: "daytotals",
};

// One entry per underlying data call. `getArgs` must be value-stable within a day so RTK can dedupe.
export const dashboardElementSources = {
    order_totals: {
        useQuery: useGetDashboardOrderTotalsQuery,
        getArgs: monthRange,
        options: { pollingInterval: 180000 },
    },
    inbound_totals: {
        useQuery: useGetDashboardInboundTotalsQuery,
        getArgs: monthRange,
        options: undefined,
    },
    support_ticket_dashboard: {
        useQuery: useGetSupportTicketDashboardQuery,
        getArgs: () => ({}),
        options: { pollingInterval: 180000 },
    },
    location_totals: {
        useQuery: useGetDashboardWarehouseLocationTotalsQuery,
        getArgs: () => undefined,
        options: undefined,
    },
};

// elementKey -> { label (i18n key), group (subject), source | selfFetching, Component }.
// Sourced elements get { totalData, data, isLoading, isFetching, sx }; self-fetching get { sx }.
export const dashboardElements = {
    // ── Orders ──
    orders_today_to_process: {
        label: "orders_today_to_process",
        group: dashboardElementGroups.ORDERS,
        source: "order_totals",
        Component: OrdersTodayToProcessCard,
        sx:{
            height:1,
            display:"flex",
            flexDirection:"column",
            amountContainer:{
                marginTop:"auto"
            }
        }
    },
    orders_future_to_process: {
        label: "orders_future_to_process",
        group: dashboardElementGroups.ORDERS,
        source: "order_totals",
        Component: OrdersFutureToProcessCard,
        sx:{
            height:1,
        }
    },
    orders_action_required: {
        label: "action_required",
        group: dashboardElementGroups.ORDERS,
        source: "order_totals",
        Component: OrdersActionRequiredCard,
        sx:{
            height:1,
            display:"flex",
            flexDirection:"column",
           
        }
    },
    orders_b2b: {
        label: "b2b",
        group: dashboardElementGroups.ORDERS,
        source: "order_totals",
        Component: OrdersB2BCard,
        sx:{
            height:1,
        }
    },
    orders_chart: {
        label: "orders",
        group: dashboardElementGroups.ORDERS,
        selfFetching: true,
        Component: OrdersChart,
    },
    orderlines_chart: {
        label: "orderlines",
        group: dashboardElementGroups.ORDERS,
        selfFetching: true,
        Component: OrderlinesChart,
    },
    order_history_chart: {
        label: "order_history",
        group: dashboardElementGroups.ORDERS,
        selfFetching: true,
        Component: OrderHistoryChart,
        sx:{
            height:1,
        }
    },
    orders_delta_panel: {
        label: "order_delta",
        group: dashboardElementGroups.ORDERS,
        selfFetching: true,
        Component: DashboardDeltaPanel,
    },

    // ── Inbounds ──
    inbounds_totals: {
        label: "totals",
        group: dashboardElementGroups.INBOUNDS,
        source: "inbound_totals",
        Component: InboundsTotals,
        sx:{
            height:1,
        }
    },
    inbounds_chart: {
        label: "inbound",
        group: dashboardElementGroups.INBOUNDS,
        selfFetching: true,
        Component: InboundChart,
    },
    inbounds_expected: {
        label: "expected_inbounds",
        group: dashboardElementGroups.INBOUNDS,
        selfFetching: true,
        Component: DaytotalsExpectedInbounds,
    },

    // ── Returns ──
    returns_chart: {
        label: "returns",
        group: dashboardElementGroups.RETURNS,
        selfFetching: true,
        Component: ReturnChart,
        sx:{
            height:1,
        }
    },

    // ── Warehouse locations ──
    locations_pick_in_use: {
        label: "pickInUse",
        group: dashboardElementGroups.LOCATIONS,
        source: "location_totals",
        Component: LocationsPickInUseCard,
    },
    locations_pick_grade: {
        label: "pickGrade",
        group: dashboardElementGroups.LOCATIONS,
        source: "location_totals",
        Component: LocationsPickGradeCard,
    },
    locations_bulk_in_use: {
        label: "bulkInUse",
        group: dashboardElementGroups.LOCATIONS,
        source: "location_totals",
        Component: LocationsBulkInUseCard,
    },
    locations_bulk_grade: {
        label: "bulkGrade",
        group: dashboardElementGroups.LOCATIONS,
        source: "location_totals",
        Component: LocationsBulkGradeCard,
    },

    // ── Service desk ──
    service_ticket_delta: {
        label: "ticket_delta",
        group: dashboardElementGroups.SERVICE,
        source: "support_ticket_dashboard",
        Component: ServiceTicketDeltaCard,
        sx:{
            height:1,
        }
    },
    service_unassigned: {
        label: "needs_attention",
        group: dashboardElementGroups.SERVICE,
        source: "support_ticket_dashboard",
        Component: ServiceUnassignedCard,
        sx:{
            height:1,
        }
    },
    service_needs_review: {
        label: "ticket_needs_review",
        group: dashboardElementGroups.SERVICE,
        source: "support_ticket_dashboard",
        Component: ServiceNeedsReviewCard,
        sx:{
            height:1,
        }
    },
    service_length_of_stay: {
        label: "length_of_stay",
        group: dashboardElementGroups.SERVICE,
        source: "support_ticket_dashboard",
        Component: ServiceLengthOfStayCard,
        sx:{
            height:1,
        }
    },
    service_responsibility: {
        label: "ticket_responsibility",
        group: dashboardElementGroups.SERVICE,
        source: "support_ticket_dashboard",
        Component: ServiceResponsibilityCard,
        sx:{
            height:1,
        }
    },

    // ── Day totals (productivity) ──
    daytotals_kpi_strip: {
        label: "processed_orders",
        group: dashboardElementGroups.DAYTOTALS,
        selfFetching: true,
        Component: DaytotalsKpiStrip,
    },
    daytotals_progression_chart: {
        label: "processed_per",
        group: dashboardElementGroups.DAYTOTALS,
        selfFetching: true,
        Component: DaytotalsProgressionChart,
    },
    daytotals_employee_table: {
        label: "detailed",
        group: dashboardElementGroups.DAYTOTALS,
        selfFetching: true,
        Component: DaytotalsEmployeeTable,
        // The table is blank without data, so its picker preview is N skeleton rows rather than the component.
        skeletonRows: 4,
    },
};
